import React, { ChangeEvent, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SelectChangeEvent, Switch, TablePagination } from '@mui/material';
import { ContentTitle } from '../../../components/wrappers/ContentTitle/ContentTitle';
import { RootState } from '../../../store/rootReducer';
import { PackageAccordion } from './PackageAccordion/PackageAccordion';
import { IPackageByQuery } from '../../../api/types';
import { changePageData, loadPackages } from '../../../store/reducers/packages/actions';
import {
  updateAvailablePackageOptions,
  setPackageSource,
  updatePackagePriceDetails,
} from '../../../store/reducers/serviceCenters/actions';
import AddPackageModal from './AddPackageModal/AddPackageModal';
import Disclaimer from './Disclaimer/Disclaimer';
import { Loading } from '../../../components/wrappers/Loading/Loading';
import { useMaintenancePackagesStyles } from './styles';
import { MaintenanceOptionTypes, PackageSourceTypes } from './constants';
import { TExpandedState, TOption, TPackageSource } from './types';
import { useAutocompleteStyles } from '../../../hooks/styling/useAutocompleteStyles';
import { useModal } from '../../../hooks/useModal/useModal';
import { useConfirm } from '../../../hooks/useConfirm/useConfirm';
import { useException } from '../../../hooks/useException/useException';
import { useSCs } from '../../../hooks/useSCs/useSCs';
import { usePagination } from '../../../hooks/usePaginations/usePaginations';
import { MaintenancePackagesTopBar } from './MaintenancePackagesTopBar';

export const MaintenancePackages = () => {
  const {
    packages: allPackages,
    packagesPaging,
    packagesPageData,
    allPackagesLoading,
  } = useSelector((state: RootState) => state.packages);
  const { loading, packagesOptionsLoading } = useSelector(
    (state: RootState) => state.serviceCenters
  );
  const [packages, setPackages] = useState<IPackageByQuery[]>([]);
  const [expanded, setExpanded] = useState<TExpandedState>({});
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isDisclaimerOpen, setDisclaimerOpen] = useState<boolean>(false);
  const [presentedOptions, setPresentedOptions] = useState<TOption[]>([]);
  const [packageSourceType, setPackageSourceType] = useState<TPackageSource>(
    PackageSourceTypes.at(0)!
  );
  const { classes } = useMaintenancePackagesStyles();
  const { classes: autocompleteClasses } = useAutocompleteStyles();
  const dispatch = useDispatch();
  const showError = useException();
  const { onOpen, onClose, isOpen } = useModal();
  const { onOpen: onOpenEdit, onClose: onCloseEdit, isOpen: isOpenEdit } = useModal();
  const { askConfirm } = useConfirm();
  const { selectedSC } = useSCs();
  const { changeRowsPerPage, changePage } = usePagination(
    (s: RootState) => s.packages.packagesPageData,
    changePageData
  );

  useEffect(() => {
    if (selectedSC) {
      dispatch(loadPackages(selectedSC.id));
      const options = MaintenanceOptionTypes.filter(item =>
        selectedSC.maintenancePackageOptionTypes?.includes(item.value)
      );
      setPresentedOptions(options);
      const source = PackageSourceTypes.find(p => p.value === selectedSC.packageSource);
      setPackageSourceType(source!);
    }
  }, [selectedSC, packagesPageData]);

  useEffect(() => {
    if (allPackages) setPackages(allPackages);
  }, [allPackages]);

  const handleAddPackage = () => {
    onOpen();
  };

  const onAccordionChange = (id: number) => {
    if (id === expanded.id) {
      setExpanded(expanded => ({ id, isOpen: !expanded.isOpen }));
    } else {
      setExpanded(() => ({ id, isOpen: true }));
    }
  };

  const onEditModalClose = () => {
    setIsEditing(false);
    onCloseEdit();
  };

  const handleAddDisclaimer = () => setDisclaimerOpen(!isDisclaimerOpen);

  const handlePriceDetailsSwitch = (_e: ChangeEvent<HTMLInputElement>, value: boolean) => {
    if (selectedSC) {
      dispatch(updatePackagePriceDetails(selectedSC.id, value, showError));
    }
  };

  const handlePackageSourceSelect = (e: SelectChangeEvent<number | string>) => {
    if (!selectedSC) return;
    const value = e.target.value;

    const selected = PackageSourceTypes.find(p => p.value === value);
    setPackageSourceType(selected!);
    dispatch(setPackageSource(selectedSC.id, selected?.value, showError));
  };

  const askRemove = useCallback(
    (value: TOption[], packagesNeededConfig: IPackageByQuery[]) => {
      if (selectedSC) {
        const newOption = value.find(
          item => !presentedOptions.find(el => +el.value === +item.value)
        );
        const packagesString = packagesNeededConfig.map((pack, index) =>
          index === packagesNeededConfig.length - 1 ? `"${pack.name}"` : `"${pack.name}", `
        );
        askConfirm({
          isRemove: false,
          title: `Please configure "${newOption?.name ?? 'this'}" option for next Maintenance Packages: ${packagesString} in order for it to display`,
          onConfirm: () =>
            dispatch(
              updateAvailablePackageOptions(
                selectedSC.id,
                value.map(item => item.value),
                showError
              )
            ),
        });
      }
    },
    [selectedSC, askConfirm, showError, presentedOptions]
  );

  const onPresentedOptionsChange = useCallback(
    (e: SyntheticEvent, value: TOption[]) => {
      if (selectedSC) {
        if (value.length > presentedOptions.length) {
          const newOption = value.find(
            item => !presentedOptions.find(el => +el.value === +item.value)
          );
          let packagesNeededConfig: IPackageByQuery[] = [];
          if (newOption) {
            packagesNeededConfig = allPackages.filter(pack =>
              pack.serviceRequestsAssigned.find(
                item => +item.type === +newOption.value && item.serviceRequestId === 0
              )
            );
          }
          if (packagesNeededConfig.length) {
            askRemove(value, packagesNeededConfig);
          } else {
            dispatch(
              updateAvailablePackageOptions(
                selectedSC.id,
                value.map(item => item.value),
                showError
              )
            );
          }
        } else {
          dispatch(
            updateAvailablePackageOptions(
              selectedSC.id,
              value.map(item => item.value),
              showError
            )
          );
        }
      }
    },
    [selectedSC, presentedOptions, askRemove, showError, allPackages]
  );

  const handleChangePage = async (
    e: React.MouseEvent<Element, MouseEvent> | null,
    pageNumber: number
  ) => {
    await changePage(e, pageNumber);
  };
  const handleChangeRows = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await changeRowsPerPage(e);
  };

  return (
    <>
      <AddPackageModal
        onClose={isEditing ? onEditModalClose : onClose}
        open={isOpen || isOpenEdit}
        isEditing={isEditing}
      />
      <MaintenancePackagesTopBar
        classes={classes}
        loading={loading}
        packagesOptionsLoading={packagesOptionsLoading}
        packageSourceType={packageSourceType}
        presentedOptions={presentedOptions}
        autocompleteClasses={autocompleteClasses}
        onPackageSourceSelect={handlePackageSourceSelect}
        onPresentedOptionsChange={onPresentedOptionsChange}
        onAddDisclaimer={handleAddDisclaimer}
        isDisclaimerOpen={isDisclaimerOpen}
        onAddPackage={handleAddPackage}
      />
      {isDisclaimerOpen ? <Disclaimer setDisclaimerOpen={setDisclaimerOpen} /> : null}
      <div className={classes.titleWrapper}>
        <ContentTitle title="Maintenance Package Pricing" />
        <div className={classes.toggleRightRow}>
          <p className={classes.toggleRightLabel}>Show Price Details</p>
          <Switch
            onChange={handlePriceDetailsSwitch}
            checked={selectedSC?.isShowPriceDetails}
            color="primary"
          />
        </div>
      </div>
      <>
        {allPackagesLoading ? (
          <Loading />
        ) : (
          packages.map((item: IPackageByQuery, index) => {
            return (
              <PackageAccordion
                setIsEditing={setIsEditing}
                onOpenEdit={onOpenEdit}
                key={item.id}
                title={item.name}
                defaultExpanded={index === 0}
                id={item.id}
                expanded={expanded.id === item.id && expanded.isOpen}
                onExpandIconClick={() => onAccordionChange(item.id)}
              />
            );
          })
        )}
      </>

      {packagesPaging?.numberOfRecords > 5 ? (
        <TablePagination
          className={classes.pagination}
          component="div"
          count={packagesPaging.numberOfRecords}
          page={packagesPageData.pageIndex}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[5, 10, 20, 50]}
          onRowsPerPageChange={handleChangeRows}
          rowsPerPage={packagesPageData.pageSize}
        />
      ) : null}
    </>
  );
};
